/**
 * Pure drill suggestion engine for the Automated Driller MVP.
 *
 * Exports two suggestion methods:
 *  - {@link suggestDrillsForClient}  — posture-based, deterministic
 *  - {@link suggestDrillsForClientAI} — worst-measurement-first scoring (falls back to
 *    posture-based when no assessment measurements are available)
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md (G9, G10, Posture → drill filter contract)
 * @see docs/features/260331-automated-driller-mvp/KICKOFF_PROMPT.md §4
 */

import type { Drill } from '@/components/drill-library/types/drill-types';
import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import type { AnalysisResultItem } from '@/lib/types';

/** Slot categories that constrain which drills are eligible per block. */
export type SlotCategory = 'warmup' | 'main-corrective' | 'accessory' | 'accessory-secondary' | 'cooldown' | 'any';

export interface SuggestedDrill {
  drill: Drill;
  score: number;
}

/** Map from slot ID prefix to category. */
export const SLOT_ID_TO_CATEGORY: Record<string, SlotCategory> = {
  'Warm-Up': 'warmup',
  'A': 'main-corrective',
  'B': 'accessory',
  'C': 'accessory-secondary',
  'D': 'cooldown',
};

/**
 * Derive a {@link SlotCategory} from a slot ID such as `"Warm-Up-1"`, `"A1"`, `"B2"`.
 */
export function slotIdToCategory(slotId: string): SlotCategory {
  if (slotId.startsWith('Warm-Up') || slotId.toLowerCase().startsWith('warm')) return 'warmup';
  if (/^Pos-/i.test(slotId) || /^Drill-/i.test(slotId)) return 'main-corrective';
  if (slotId.startsWith('A')) return 'main-corrective';
  if (slotId.startsWith('B')) return 'accessory';
  if (slotId.startsWith('C')) return 'accessory-secondary';
  if (slotId.startsWith('D')) return 'cooldown';
  return 'any';
}

/**
 * Returns the tag values for a given category key, normalising string | string[] to string[].
 */
function getDrillTagValues(drill: Drill, key: string): string[] {
  const raw = drill.tags?.[key];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return [raw];
}

/**
 * Check whether a drill's mainCategory or Biomechanical Intent tags match the slot category.
 */
function drillFitsSlotCategory(drill: Drill, category: SlotCategory): boolean {
  if (category === 'any') return true;

  const main = (drill.mainCategory || '').toLowerCase();
  const bioIntents = getDrillTagValues(drill, 'Biomechanical Intent').map((s) => s.toLowerCase());

  switch (category) {
    case 'warmup':
      return (
        main.includes('warm') ||
        main.includes('mobility') ||
        main.includes('activation') ||
        getDrillTagValues(drill, 'Domain').some((d) => d.toLowerCase().includes('warm'))
      );
    case 'main-corrective':
      return (
        main.includes('corrective') ||
        main.includes('positional') ||
        main.includes('main') ||
        bioIntents.some((b) =>
          b.includes('occupancy') || b.includes('expansion') || b.includes('propulsion')
        )
      );
    case 'accessory':
      return (
        main.includes('accessory') ||
        main.includes('strength') ||
        main.includes('weightroom')
      );
    case 'accessory-secondary':
      return (
        main.includes('accessory') ||
        main.includes('secondary') ||
        main.includes('stability')
      );
    case 'cooldown':
      return (
        main.includes('cool') ||
        main.includes('stretch') ||
        main.includes('breathe') ||
        main.includes('breathing')
      );
    default:
      return true;
  }
}

/**
 * Compute a numeric relevance score for a single drill against the suggestion filters.
 *
 * Scoring breakdown (higher = more relevant):
 * - Phase match: +20 (required for most; phase-less drills pass filter but score 0 here)
 * - Posture match: +15 per matching posture label
 * - Movement limitation overlap: +10 per exact match, +5 per substring match
 * - Biomechanical intent overlap: +8 per match
 * - Lateralization bonus: +5 when lateralized side matches
 * - Pain modifier: −2 × painLevel (NRS 0–10, Phase 6.3) for drills tagged
 *   high-intensity, when painLevel > 0. Proportional rescale of the legacy
 *   −10 × painLevel (0–2 scale) so the max penalty stays −20 (old: painLevel=2
 *   → −20; new: painLevel=10 → −20). `filters.painLevel` must already be the
 *   normalized 0–10 value — see `DrillSuggestionFilters.painLevel`.
 * - Lifting experience modifier: −8 for advanced drills when liftingExperience < 1 (untouched)
 */
function scoreDrill(drill: Drill, filters: DrillSuggestionFilters): number {
  let score = 0;

  // --- Phase ---
  const drillPhases = getDrillTagValues(drill, 'Phase');
  const phaseMatches =
    drillPhases.length === 0 || drillPhases.includes(filters.phaseTag);
  if (!phaseMatches) return -Infinity; // hard exclude wrong phase
  if (drillPhases.length > 0 && drillPhases.includes(filters.phaseTag)) score += 20;

  // --- Posture ---
  const drillPostures = getDrillTagValues(drill, 'Posture');
  for (const label of filters.postureLabels) {
    if (drillPostures.includes(label)) score += 15;
  }

  // --- Infrasternal Angle (ISA) ---
  if (filters.isaPattern) {
    const pelvisSpineTags = getDrillTagValues(drill, 'Pelvis & Spine');
    if (filters.isaPattern === 'Narrow') {
      if (pelvisSpineTags.includes('ISA-narrow')) score += 15;
    } else if (filters.isaPattern === 'Wide') {
      if (pelvisSpineTags.includes('ISA-wide') || pelvisSpineTags.includes('ISA-flared')) score += 15;
    }
  }

  // --- Movement Limitation ---
  const drillLimitations = getDrillTagValues(drill, 'Movement Limitation');
  for (const limitation of filters.movementLimitations) {
    const limitLower = limitation.toLowerCase();
    let matched = false;
    for (const dl of drillLimitations) {
      const dlLower = dl.toLowerCase();
      if (dlLower === limitLower) {
        score += 10;
        matched = true;
        break;
      }
      if (dlLower.includes(limitLower) || limitLower.includes(dlLower)) {
        score += 5;
        matched = true;
        break;
      }
    }
    if (!matched) {
      // check keywords within the limitation string vs drill limitation strings
      const keywords = limitLower.split(/[\s,/]+/).filter((w) => w.length > 3);
      for (const kw of keywords) {
        if (drillLimitations.some((dl) => dl.toLowerCase().includes(kw))) {
          score += 2;
          break;
        }
      }
    }
  }

  // --- Biomechanical Intent ---
  const drillBioIntents = getDrillTagValues(drill, 'Biomechanical Intent');
  const mainCatLower = (drill.mainCategory || '').toLowerCase();
  for (const intent of filters.biomechanicalIntents) {
    const intentLower = intent.toLowerCase();
    if (
      drillBioIntents.some((b) => b.toLowerCase().includes(intentLower)) ||
      mainCatLower.includes(intentLower)
    ) {
      score += 8;
    }
  }

  // --- Lateralization tiebreaker ---
  if (filters.lateralized) {
    const drillLat = getDrillTagValues(drill, 'Lateralization');
    if (drillLat.includes(filters.lateralized)) score += 5;
  }

  // --- Pain level modifier (NRS 0 = no pain, 10 = worst; Phase 6.3) ---
  const painLevel = filters.painLevel ?? 0;
  if (painLevel > 0) {
    const intensityTags = getDrillTagValues(drill, 'Intensity');
    const isHighLoad = intensityTags.some((t) =>
      t.toLowerCase().includes('high') || t.toLowerCase().includes('heavy')
    );
    if (isHighLoad) score -= 2 * painLevel;
  }

  // --- Lifting experience modifier (0 = novice, 3 = advanced) ---
  const liftExp = filters.liftingExperience ?? 1;
  const skillTags = getDrillTagValues(drill, 'Skill Level');
  const isAdvanced = skillTags.some((t) => t.toLowerCase().includes('advanced'));
  if (isAdvanced && liftExp < 1) score -= 8;

  return score;
}

/**
 * Suggest drills for a given slot category from the full drill catalogue, ranked by relevance.
 *
 * This is a **pure function** — no I/O, no side effects.
 *
 * @param filters     Drill-taxonomy-aligned filters from `buildDrillSuggestionFilters`.
 * @param slotCategory The slot category (e.g. `'warmup'`, `'main-corrective'`).
 * @param drills      The full drill catalogue.
 * @param topN        Max number of drills to return (default 5).
 */
export function suggestDrillsForClient(
  filters: DrillSuggestionFilters,
  slotCategory: SlotCategory,
  drills: Drill[],
  topN = 5
): SuggestedDrill[] {
  const scored: SuggestedDrill[] = [];

  for (const drill of drills) {
    // Slot-category gate (soft — drills with `any` category or no category always pass)
    if (!drillFitsSlotCategory(drill, slotCategory)) continue;

    const score = scoreDrill(drill, filters);
    if (score === -Infinity) continue; // hard-excluded (wrong phase)

    scored.push({ drill, score });
  }

  // Sort descending by score, then stable by drill name for determinism
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.drill.name.localeCompare(b.drill.name);
  });

  return scored.slice(0, topN);
}

// ---------------------------------------------------------------------------
// AI-enhanced suggestion — worst-measurement-first scoring
// ---------------------------------------------------------------------------

/**
 * Map from SoP test name to drill movement-limitation keywords.
 * Used to translate failing assessment tests → drill filter terms.
 */
const TEST_TO_LIMITATION_KEYWORDS: Record<string, string[]> = {
  ShoulderIR: ['anterior rib cage expansion', 'ir', 'internal rotation'],
  ShoulderER: ['posterior rib cage expansion', 'er', 'external rotation'],
  FemoralIR: ['lower body ir', 'femoral ir', 'internal rotation'],
  FemoralER: ['lower body er', 'femoral er', 'external rotation'],
  StraightLegRaise: ['straight leg raise', 'slr', 'hip flexion', 'posterior expansion'],
  HipExtension: ['hip extension', 'grounding', 'hip extension'],
  PelvicRotation: ['pelvic', 'pelvis', 'femoral ir'],
  HipAbduction: ['hip abduction', 'pelvic', 'femoral'],
  OberTest: ['hip abduction', 'it band', 'lateral'],
  CervicalRotation: ['cervical', 'neck', 'upper body'],
  ShoulderAbduction: ['shoulder abduction', 'upper body'],
};

/**
 * Compute a severity-weighted score bonus from failing SoP tests.
 *
 * Items with the largest **deviation below their nominal** (worst measurements)
 * contribute the highest bonus to drills that address that movement limitation.
 *
 * @returns Extra score to add on top of the base posture score.
 */
function scoreFromWorstMeasurements(
  drill: Drill,
  worstItems: Array<{ testKey: string; deviationPct: number }>
): number {
  let bonus = 0;
  const drillLimitations = getDrillTagValues(drill, 'Movement Limitation').map((s) =>
    s.toLowerCase()
  );
  const drillBioIntents = getDrillTagValues(drill, 'Biomechanical Intent').map((s) =>
    s.toLowerCase()
  );
  const allDrillText = [...drillLimitations, ...drillBioIntents, drill.name.toLowerCase()];

  for (const { testKey, deviationPct } of worstItems) {
    const keywords = TEST_TO_LIMITATION_KEYWORDS[testKey] ?? [];
    for (const kw of keywords) {
      if (allDrillText.some((t) => t.includes(kw))) {
        // Scale bonus by deviation severity: worse = higher boost (max 30 pts)
        bonus += Math.min(30, 5 + deviationPct * 20);
        break;
      }
    }
  }

  return bonus;
}

/**
 * Extract normalised "worst measurement" items from the client's latest SoP analysis.
 *
 * Returns the top-N worst failing tests sorted by deviation (farthest below nominal first).
 */
export function extractWorstMeasurements(
  analysisItems: AnalysisResultItem[],
  topN = 5
): Array<{ testKey: string; deviationPct: number; label: string }> {
  const failing: Array<{ testKey: string; deviationPct: number; label: string }> = [];

  for (const item of analysisItems) {
    if (item.nominal <= 0) continue; // skip items with no nominal threshold

    const leftFailing = !item.isLeftPassing && item.leftVal !== null;
    const rightFailing = !item.isRightPassing && item.rightVal !== null;

    if (leftFailing || rightFailing) {
      // Use the worse of the two sides
      const worstVal = Math.min(
        leftFailing && item.leftVal !== null ? item.leftVal : Infinity,
        rightFailing && item.rightVal !== null ? item.rightVal : Infinity
      );
      const deviationPct =
        worstVal !== Infinity ? Math.max(0, (item.nominal - worstVal) / item.nominal) : 0;

      failing.push({
        testKey: item.test,
        deviationPct,
        label: item.test,
      });
    }
  }

  // Sort by worst deviation first
  failing.sort((a, b) => b.deviationPct - a.deviationPct);
  return failing.slice(0, topN);
}

/**
 * AI-enhanced drill suggestion — prioritises drills that address the **worst failing
 * SoP measurements** for the client. Falls back to {@link suggestDrillsForClient}
 * scoring when no assessment data is available.
 *
 * This is a **pure function** — no I/O, no side effects.
 *
 * @param filters        Drill-taxonomy-aligned filters (same as suggestDrillsForClient).
 * @param slotCategory   Slot category constraint.
 * @param drills         Full drill catalogue.
 * @param analysisItems  Flat list of `AnalysisResultItem` from the client's latest SoP
 *                       assessment (left + right + intact arrays combined). Pass `[]` for
 *                       no-data fallback.
 * @param topN           Max results to return (default 5).
 */
export function suggestDrillsForClientAI(
  filters: DrillSuggestionFilters,
  slotCategory: SlotCategory,
  drills: Drill[],
  analysisItems: AnalysisResultItem[],
  topN = 5
): SuggestedDrill[] {
  // If no measurement data, fall back to the deterministic method
  if (analysisItems.length === 0) {
    return suggestDrillsForClient(filters, slotCategory, drills, topN);
  }

  const worstItems = extractWorstMeasurements(analysisItems, 5);
  const scored: SuggestedDrill[] = [];

  for (const drill of drills) {
    if (!drillFitsSlotCategory(drill, slotCategory)) continue;

    const baseScore = scoreDrill(drill, filters);
    if (baseScore === -Infinity) continue;

    const measurementBonus = scoreFromWorstMeasurements(drill, worstItems);
    scored.push({ drill, score: baseScore + measurementBonus });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.drill.name.localeCompare(b.drill.name);
  });

  return scored.slice(0, topN);
}
